import { useState, useEffect } from "react";
import api, { formatApiError } from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";

const STATUSES = ["Placed", "Confirmed", "Shipped", "Delivered", "Cancelled"];
const API_BASE = `/api`;

export default function AdminOrders() {
  const [orders, setOrders] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [payFilter, setPayFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [notes, setNotes] = useState({});
  const [shipForms, setShipForms] = useState({});
  const [shipResults, setShipResults] = useState({});
  const [shipLoading, setShipLoading] = useState(null);

  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const createShipment = async (orderNumber) => {
    setShipLoading(orderNumber);
    try {
      const { data } = await api.post(`/admin/orders/${orderNumber}/ship`, shipForms[orderNumber] || {});
      setShipResults((s) => ({ ...s, [orderNumber]: `AWB ${data.awb_code} · ${data.courier_name || "Courier assigned"}` }));
      toast.success("Shipment created");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setShipLoading(null);
    }
  };

  const trackShipment = async (orderNumber) => {
    setShipLoading(orderNumber + "-track");
    try {
      const { data } = await api.get(`/admin/orders/${orderNumber}/track-shipment`);
      setShipResults((s) => ({ ...s, [orderNumber]: `${data.courier || "Courier"} · ${data.status}` }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setShipLoading(null);
    }
  };

  const updateStatus = async (orderNumber, status) => {
    try {
      await api.put(`/admin/orders/${orderNumber}/status`, { status });
      toast.success(`Order ${orderNumber} → ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteOrder = async (orderNumber) => {
    if (!window.confirm(`Delete order ${orderNumber} permanently? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/orders/${orderNumber}`);
      toast.success(`Order ${orderNumber} deleted`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!orders) return <div className="h-40 bg-muted animate-pulse" />;

  const FILTERS = [
    ["all", "All"], ["new", "New"], ["Shipped", "Shipped"], ["Delivered", "Delivered"], ["Cancelled", "Cancelled"],
  ];
  const filtered = orders.filter((o) => {
    if (filter === "new" && !["Placed", "Confirmed"].includes(o.status)) return false;
    if (["Shipped", "Delivered", "Cancelled"].includes(filter) && o.status !== filter) return false;
    if (payFilter === "cod" && o.payment_method !== "cod") return false;
    if (payFilter === "online" && o.payment_method !== "razorpay") return false;
    if (payFilter === "paid" && o.payment_status !== "paid") return false;
    if (payFilter === "unpaid" && o.payment_status === "paid") return false;
    if (dateFrom && o.created_at < dateFrom) return false;
    if (dateTo && o.created_at > dateTo + "T23:59:59") return false;
    if (search) {
      const q = search.toLowerCase();
      return o.order_number.toLowerCase().includes(q) || o.customer.name.toLowerCase().includes(q) || o.customer.email.toLowerCase().includes(q);
    }
    return true;
  });

  const saveNote = async (orderNumber) => {
    try {
      await api.put(`/admin/orders/${orderNumber}/note`, { note: notes[orderNumber] || "" });
      toast.success("Note saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-orders">
      <h1 className="font-serif text-3xl font-semibold mb-6">Orders <span className="text-sm text-muted-foreground font-sans font-normal">({filtered.length} of {orders.length})</span></h1>
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {FILTERS.map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)}
            className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors duration-300 ${filter === v ? "bg-[#111] text-[#FAF9F6] border-[#111]" : "border-black/15 hover:border-gold"}`}
            data-testid={`filter-${v.toLowerCase()}`}>
            {l}
          </button>
        ))}
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search order #, name, email..."
          className="ml-auto border border-black/15 bg-white px-3 py-2 text-sm w-64 outline-none focus:border-gold" data-testid="order-search" />
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {[["all", "All Payments"], ["cod", "COD"], ["online", "Online"], ["paid", "Paid"], ["unpaid", "Unpaid"]].map(([v, l]) => (
          <button key={v} onClick={() => setPayFilter(v)}
            className={`px-3 py-1.5 text-[10px] uppercase tracking-widest border transition-colors duration-300 ${payFilter === v ? "bg-[#111] text-[#FAF9F6] border-[#111]" : "border-black/15 hover:border-gold"}`}
            data-testid={`pay-filter-${v}`}>
            {l}
          </button>
        ))}
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="ml-auto border border-black/15 bg-white px-2 py-1.5 text-xs outline-none focus:border-gold" data-testid="date-from" />
        <span className="text-xs text-muted-foreground">to</span>
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="border border-black/15 bg-white px-2 py-1.5 text-xs outline-none focus:border-gold" data-testid="date-to" />
      </div>
      {filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground bg-white border border-black/10 p-12 text-center" data-testid="no-orders">No orders match.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {filtered.map((o) => (
            <div key={o.id} className="bg-white border border-black/10" data-testid={`admin-order-${o.order_number}`}>
              <div onClick={() => setExpanded(expanded === o.id ? null : o.id)} className="w-full flex flex-wrap items-center gap-3 p-4 text-left cursor-pointer" role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && setExpanded(expanded === o.id ? null : o.id)} data-testid={`order-toggle-${o.order_number}`}>
                <span className="font-medium text-sm">{o.order_number}</span>
                <span className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString("en-IN")}</span>
                <span className="text-sm flex-1">{o.customer.name}</span>
                <span className="text-sm font-semibold">₹{o.total.toLocaleString("en-IN")}</span>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${o.payment_status === "paid" ? "bg-green-100 text-green-900" : "bg-gold-light text-[#111]"}`}>
                  {o.payment_method === "cod" ? "COD" : o.payment_status}
                </span>
                <span className="text-[10px] uppercase tracking-widest px-2 py-1 bg-[#111] text-[#FAF9F6]">{o.status}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); deleteOrder(o.order_number); }}
                  className="text-[10px] uppercase tracking-widest px-2 py-1 border border-red-300 text-destructive hover:bg-red-50 transition-colors duration-300"
                  data-testid={`delete-order-${o.order_number}`}>Delete</button>
              </div>
              {expanded === o.id && (
                <div className="border-t border-black/10 p-4 grid md:grid-cols-2 gap-6" data-testid={`order-details-${o.order_number}`}>
                  <div className="text-sm">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Items</p>
                    {o.items.map((i) => (
                      <p key={i.product_id} className="py-0.5">{i.name} × {i.qty} — ₹{(i.price * i.qty).toLocaleString("en-IN")}</p>
                    ))}
                    {o.discount > 0 && (
                      <p className="py-0.5 text-green-800" data-testid={`order-discount-${o.order_number}`}>
                        Discount ({o.campaign_name || o.coupon_code}) — −₹{o.discount.toLocaleString("en-IN")}
                      </p>
                    )}
                    {o.cod_fee > 0 && (
                      <p className="py-0.5 text-muted-foreground" data-testid={`order-cod-fee-${o.order_number}`}>
                        COD handling fee — ₹{o.cod_fee.toLocaleString("en-IN")}
                      </p>
                    )}
                    <p className="py-0.5 font-medium">Total — ₹{o.total.toLocaleString("en-IN")}</p>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mt-4 mb-2">Delivery</p>
                    <p>{o.customer.name} · {o.customer.phone}</p>
                    <p className="text-muted-foreground">{o.address.line1} {o.address.line2}, {o.address.city}, {o.address.state} - {o.address.pincode}</p>
                    <p className="text-muted-foreground">{o.customer.email}</p>
                    {o.status === "Cancelled" && o.cancellation_reason && (
                      <div className="mt-3 bg-red-50 border border-red-200 p-3" data-testid={`admin-cancel-reason-${o.order_number}`}>
                        <p className="text-xs uppercase tracking-widest text-destructive">Cancelled{o.cancelled_by ? ` by ${o.cancelled_by}` : ""}{o.cancelled_at ? ` · ${new Date(o.cancelled_at).toLocaleString("en-IN")}` : ""}</p>
                        <p className="text-sm mt-1">Reason: {o.cancellation_reason}</p>
                        {o.refund_status && <p className="text-xs mt-1">Refund status: {o.refund_status}</p>}
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Update Status</p>
                    <Select value={o.status} onValueChange={(v) => updateStatus(o.order_number, v)}>
                      <SelectTrigger className="rounded-sm" data-testid={`status-select-${o.order_number}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`status-${s.toLowerCase()}`}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-3">Customer gets an email on every status change.</p>
                  </div>
                  <div className="md:col-span-2 border-t border-black/10 pt-4">
                    <div className="flex flex-wrap gap-3 mb-4">
                      <a href={`${API_BASE}/admin/orders/${o.order_number}/invoice`} target="_blank" rel="noreferrer"
                        className="border border-black/20 px-4 py-2 text-xs uppercase tracking-widest hover:border-gold hover:text-gold transition-colors duration-300"
                        data-testid={`invoice-${o.order_number}`}>GST Invoice</a>
                      <a href={`${API_BASE}/admin/orders/${o.order_number}/packing-slip`} target="_blank" rel="noreferrer"
                        className="border border-black/20 px-4 py-2 text-xs uppercase tracking-widest hover:border-gold hover:text-gold transition-colors duration-300"
                        data-testid={`packing-slip-${o.order_number}`}>Packing Slip</a>
                    </div>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Courier (Shiprocket)</p>
                    {o.awb_code ? (
                      <div className="flex flex-wrap items-center gap-3 text-sm">
                        <span data-testid={`awb-${o.order_number}`}>AWB: <strong>{o.awb_code}</strong> · {o.courier_name || "Courier"}</span>
                        <button onClick={() => trackShipment(o.order_number)} disabled={shipLoading}
                          className="border border-black/20 px-4 py-2 text-xs uppercase tracking-widest hover:border-gold hover:text-gold transition-colors duration-300"
                          data-testid={`track-courier-${o.order_number}`}>
                          {shipLoading === o.order_number + "-track" ? "Checking..." : "Track Courier"}
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2">
                        {[["weight_kg", "Weight kg"], ["length_cm", "L cm"], ["breadth_cm", "B cm"], ["height_cm", "H cm"]].map(([k, ph]) => (
                          <input key={k} placeholder={ph} type="number" min="0" step="0.1"
                            value={shipForms[o.order_number]?.[k] || ""}
                            onChange={(e) => setShipForms((s) => ({ ...s, [o.order_number]: { ...(s[o.order_number] || {}), [k]: e.target.value } }))}
                            className="w-20 border border-black/15 px-2 py-2 text-xs outline-none focus:border-gold"
                            data-testid={`ship-${k}-${o.order_number}`} />
                        ))}
                        <button onClick={() => createShipment(o.order_number)} disabled={shipLoading === o.order_number}
                          className="bg-[#111] text-[#FAF9F6] px-4 py-2 text-xs uppercase tracking-widest hover:bg-gold hover:text-[#111] transition-colors duration-300 disabled:opacity-50"
                          data-testid={`create-shipment-${o.order_number}`}>
                          {shipLoading === o.order_number ? "Creating..." : "Create Shipment"}
                        </button>
                      </div>
                    )}
                    {shipResults[o.order_number] && <p className="text-xs text-gold mt-2" data-testid={`ship-result-${o.order_number}`}>{shipResults[o.order_number]}</p>}
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mt-4 mb-2">Admin Note (private)</p>
                    <div className="flex gap-2">
                      <input value={notes[o.order_number] ?? o.admin_note ?? ""} onChange={(e) => setNotes((n) => ({ ...n, [o.order_number]: e.target.value }))}
                        placeholder="e.g. Customer called, deliver after Monday"
                        className="flex-1 border border-black/15 px-3 py-2 text-sm outline-none focus:border-gold" data-testid={`note-input-${o.order_number}`} />
                      <button onClick={() => saveNote(o.order_number)} className="border border-black/20 px-4 py-2 text-xs uppercase tracking-widest hover:border-gold hover:text-gold transition-colors duration-300" data-testid={`note-save-${o.order_number}`}>Save</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
